import React from "react";
import { Heart } from "lucide-react";
import PowerChart from "./PowerChart";
import { averageHr, formatLapTime } from "../../utils/freeTraining";
import { cn } from "../../lib/utils";
import { ZONE_COLOURS } from "../../utils/heartRateZones";

// Free training's second view (#146, #157): the Coach's own chart, over ten
// minutes rather than one since there's no next step to show. Heart rate
// only until a machine is paired, then power under it. Above it, the figures
// you'd glance at: heart rate now, average and max, and your zone.

// HeartRateZones' shades, zone 1 to 5.
// Zone colours shared app-wide.
const ZONE_SHADES = ZONE_COLOURS.slice(1);

export default function FreeTrainingGraph({ bpm, watts, hr, zone, subscribe, hrSource }) {
  const avg = hr ? averageHr(hr) : null;
  const inZone = zone ? hr?.zoneSeconds?.[zone] || 0 : 0;
  const hasPower = watts != null;

  return (
    <div className="flex w-full flex-col justify-center gap-3 px-1 text-left">
      <div className="flex items-end justify-between gap-3">
        <p className="flex items-baseline gap-1.5 font-timer tabular-nums leading-none">
          <span className="text-5xl font-semibold text-white">{bpm != null ? Math.round(bpm) : "—"}</span>
          <span className="text-xs font-semibold uppercase tracking-wider text-white/50">bpm</span>
          {hasPower && (
            <>
              <span className="ml-2 text-2xl font-semibold text-yellow-400">{Math.round(watts)}</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-white/50">W</span>
            </>
          )}
        </p>
        {avg != null && (
          <p className="grid gap-0.5 text-right text-xs text-white/50">
            <span>
              {"Avg "}
              <b className="font-timer font-semibold text-white">{avg}</b>
            </span>
            <span>
              {"Max "}
              <b className="font-timer font-semibold text-white">{hr.max}</b>
            </span>
          </p>
        )}
      </div>

      {bpm == null && avg == null ? (
        <p className="text-sm text-white/50">
          <Heart className="mr-1.5 inline h-3.5 w-3.5 -translate-y-px" aria-hidden="true" />
          {"No heart rate connected"}
        </p>
      ) : (
        <div>
          <div className="grid grid-cols-5 gap-[3px]" aria-hidden="true">
            {ZONE_SHADES.map((colour, i) => (
              <span
                key={colour}
                className={cn("h-2 rounded-full", !(zone && i < zone) && "bg-white/5")}
                style={zone && i < zone ? { background: colour } : undefined}
              />
            ))}
          </div>
          <p className="mt-1 flex justify-between text-[11px] text-white/40">
            <span>
              {"Zone "}
              <b className="text-white">{zone || "—"}</b>
            </span>
            {zone ? (
              <span>
                <b className="font-timer text-white">{formatLapTime(inZone)}</b>
                {" in this zone"}
              </span>
            ) : null}
          </p>
        </div>
      )}

      <div>
        <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-white/40">
          {hasPower ? "Power · HR · last 10 min" : "HR · last 10 min"}
        </p>
        <div className="h-24 overflow-hidden rounded-lg border border-white/5 bg-white/[0.03]">
          <PowerChart
            subscribe={subscribe}
            hr={hrSource}
            hrBpm={bpm}
            windowSeconds={600}
            className="block h-full w-full"
          />
        </div>
      </div>
    </div>
  );
}
